import { theme } from "nectar-ui";
import {
  DASHBOARD_CHART_TYPES,
  CHART_ORIENTATIONS,
} from "@utils/constants/dashboard/dashboardModuleConstants";
import { apexChartInvertedSeriesClickHandler } from "@utils/helpers/dashboardHelpers";
import { ChartOrientation } from "@customTypes/chart";
import { getCalendarActivityColors } from "../helpers";
import { CalendarActivityTypes } from "@customTypes/filter";
import { ApexOptions } from "apexcharts";

export const MAXIMUM_RESPONSIBLE_NUMBER = 8;

const getBaseChartOptions = (calendarActivityTypes: CalendarActivityTypes[]): ApexOptions => ({
  chart: {
    id: DASHBOARD_CHART_TYPES.IN_PROGRESS_ACTIVITIES_BY_RESPONSIBLE.chart,
    type: "bar",
    fontFamily: theme.nectar.fontStack.default,
    stacked: true,
    toolbar: {
      show: false,
    },
    events: {
      legendClick: apexChartInvertedSeriesClickHandler,
    },
  },
  colors: getCalendarActivityColors(calendarActivityTypes),
  dataLabels: {
    style: {
      colors: [theme.nectar.colors.gray900],
    },
  },
  stroke: {
    width: 1,
    colors: ["#fff"],
  },
  fill: {
    opacity: 1,
  },
  legend: {
    position: "bottom",
    horizontalAlign: "center",
    offsetY: 8,
    itemMargin: {
      horizontal: 14,
    },
    onItemClick: {
      toggleDataSeries: false,
    },
  },
});

const getVerticalOrientationOptions = (responsible: string[]): ApexOptions => {
  const commonAxisLabelStyle = {
    fontSize: "14px",
    fontWeight: 300,
    colors: theme.nectar.colors.gray500,
  };

  return {
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 4,
        borderRadiusWhenStacked: "last",
        borderRadiusApplication: "end",
        dataLabels: {
          total: {
            enabled: true,
            style: {
              fontSize: "14px",
              color: theme.nectar.colors.gray900,
              fontWeight: 900,
            },
          },
        },
      },
    },
    xaxis: {
      categories: responsible,
      labels: {
        rotate: 0,
        trim: true,
        hideOverlappingLabels: false,
        style: commonAxisLabelStyle,
      },
    },
    yaxis: {
      labels: {
        style: commonAxisLabelStyle,
        formatter: (value: number) => Math.round(value).toString(),
      },
    },
    grid: {
      show: true,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
  };
};

const getHorizontalOrientationOptions = (responsible: string[]): ApexOptions => {
  const commonAxisLabelStyle = {
    fontSize: "14px",
    fontWeight: 300,
    colors: theme.nectar.colors.gray500,
  };

  return {
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        borderRadiusWhenStacked: "last",
        borderRadiusApplication: "end",
        dataLabels: {
          total: {
            enabled: true,
            style: {
              fontSize: "14px",
              color: theme.nectar.colors.gray900,
              fontWeight: 900,
            },
          },
        },
      },
    },
    xaxis: {
      categories: responsible,
      labels: {
        style: commonAxisLabelStyle,
        formatter: (value) => Math.round(+value).toString(),
      },
    },
    yaxis: {
      labels: {
        style: commonAxisLabelStyle,
      },
    },
    grid: {
      show: true,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
    },
  };
};

const getOrientationOptions = (
  orientation: ChartOrientation,
  responsible: string[]
): ApexOptions => {
  if (orientation === CHART_ORIENTATIONS.VERTICAL) {
    return getVerticalOrientationOptions(responsible);
  }

  if (orientation === CHART_ORIENTATIONS.HORIZONTAL) {
    return getHorizontalOrientationOptions(responsible);
  }

  return {};
};

export const getChartOptions = (
  responsible: string[],
  calendarActivityTypes: CalendarActivityTypes[],
  chartOrientation: ChartOrientation
): ApexOptions => {
  const baseOptions = getBaseChartOptions(calendarActivityTypes);
  const orientationOptions = getOrientationOptions(chartOrientation, responsible);

  return {
    ...baseOptions,
    ...orientationOptions,
  };
};
